import { computed } from 'vue'

import { OlympusClient } from '@/OlympusClient'

import { type Location } from '../../types'
import { useCoreStore } from '../useCoreStore'

import { useActiveBrand } from './useActiveBrand'

/**
 * Catering FAQ answers to write. Omitting a field leaves it alone, which is what
 * keeps `includeDeliveryMinimum`'s stored `NULL` (auto) intact for a merchant
 * who never touched the toggle — send only the fields the user changed.
 *
 * `dietaryAccommodation` is restated rather than taken from the generated body
 * type: the endpoint validates it with `z.nativeEnum`, which the client
 * generator degrades to `unknown`.
 */
export interface CateringFaqSettingsPatch {
  dietaryAccommodation?: 'none' | 'limited' | 'yes'
  includeDeliveryMinimum?: boolean
  providesPlates?: boolean
  providesNapkins?: boolean
  providesCutlery?: boolean
}

/**
 * Operations on the current activeBrand's locations
 */
export const useLocations = () => {
  const {
    locations,
    locationsWithoutStripeConnected,
    locationsWithoutYextConnected,
  } = useActiveBrand()
  const { addBrandLocation, updateLocationData } = useCoreStore()

  const getLocationById = computed(() => {
    return (locationId: string) =>
      locations.value.find((loc) => loc.id === locationId)
  })

  const getLocationNameById = computed(() => {
    return (locationId: string) => getLocationById.value(locationId)?.name ?? ''
  })

  const getLocationPosById = computed(() => {
    return (locationId: string) =>
      getLocationById.value(locationId)?.pos ?? undefined
  })

  const refreshLocationData = async (
    locationId: string | undefined
  ): Promise<void> => {
    if (!locationId) return
    const location = await OlympusClient.locations.location.getLocation({
      params: { locationId },
    })
    updateLocationData(locationId, location)
  }

  const addLocation = (location: Location) => {
    addBrandLocation({
      ...location,
      // initializing a new brand location with false on all integration and
      // payment fronts since we only get Location data back when adding a new location
      kioskSettings: {
        isUsingOwnerKiosk: false,
      },
      posSettings: {
        isUsingDoordashIntegration: false,
        isUsingGrubhubIntegration: false,
        isUsingOwnerPOS: false,
        isUsingUberEatsIntegration: false,
      },
    })
  }

  const updateLocation = async ({
    locationId,
    update,
  }: {
    locationId: string
    update: Partial<Location>
  }) => {
    const location = await OlympusClient.locations.location.updateLocation({
      body: {
        ...update,
        // if update.address is null, set as undefined for the endpoint signature
        // since endpoint param is not nullable but type is
        address: update.address ?? undefined,
      },
      params: {
        locationId,
      },
    })

    updateLocationData(locationId, location)
  }

  const updateLocationCateringSettings = async ({
    locationId,
    faqSettings,
    reviewCateringOrders,
    maximumOrderWindowDays,
    minimumLeadTimeHours,
  }: {
    locationId: string
    faqSettings?: CateringFaqSettingsPatch
    reviewCateringOrders?: boolean
    maximumOrderWindowDays?: number
    minimumLeadTimeHours?: number
  }) => {
    const location =
      await OlympusClient.locations.location.updateCateringSettings({
        body: {
          faqSettings,
          maximumOrderWindowDays,
          minimumLeadTimeHours,
          reviewCateringOrders,
        },
        params: { locationId },
      })

    updateLocationData(locationId, location)
  }

  const updateLocationCateringNotificationRecipients = async ({
    locationId,
    recipients,
  }: {
    locationId: string
    recipients: Array<{ email: string | null; phone: string | null }>
  }) => {
    const location =
      await OlympusClient.locations.location.updateCateringNotificationRecipients(
        {
          body: { recipients },
          params: { locationId },
        }
      )

    updateLocationData(locationId, location)
  }

  const updateLocationGuestBearing = async ({
    locationId,
    online,
    store,
  }: {
    locationId: string
    online: { guestBearing: number }
    store?: { guestBearing: number }
  }) => {
    const response = await OlympusClient.locations.location.updateGuestBearing({
      body: {
        online,
        store,
      },
      params: { locationId },
    })

    updateLocationData(locationId, { ownerFeeSettings: response.settings })
  }

  const updateLocationLinks = async ({
    locationId,
    update,
  }: {
    locationId: string
    update: Partial<Location['funnel']['links']>
  }) => {
    const locationToUpdate = getLocationById.value(locationId)
    if (!locationToUpdate) {
      throw new Error(
        `location with id ${locationId} not found on active brand`
      )
    }
    const targetLinks = locationToUpdate.funnel.links
    if (targetLinks) {
      const updatedLinks = { ...targetLinks, ...update }
      const location = await OlympusClient.locations.location.funnel.setLinks({
        body: updatedLinks,
        params: {
          locationId,
        },
      })

      updateLocationData(locationId, location)
    }
  }

  const updateLocationListingSyncTime = async ({
    locationId,
    updatedTime,
  }: {
    locationId: string
    updatedTime: string
  }) => {
    updateLocationData(locationId, { listingsUpdatedAt: updatedTime })
  }

  const updateLocationOtter = async ({
    locationId,
    otterStoreId,
    openItemInjection,
  }: {
    locationId: string
    otterStoreId: string
    openItemInjection?: boolean
  }) => {
    const location = await OlympusClient.locations.location.linkOtterStore({
      body: {
        openItemInjection,
        otterStoreId,
      },
      params: {
        locationId,
      },
    })
    updateLocationData(locationId, location)
  }

  type OwnerFeeSettingsUpdate = {
    fee: number
    feeType: 'fixed' | 'percent'
    guestBearing: number
  } & (
    | {
        feeCap: number
        subtotalCap?: never
      }
    | {
        feeCap?: never
        subtotalCap: number
      }
  )
  interface OwnerFeeDeliveryOverrideUpdate {
    fee: number
    feeType: 'fixed' | 'percent'
    guestBearing: number
    feeCap: number
    subtotalCap: number
  }
  const updateLocationOwnerFeeSettings = async ({
    locationId,
    online,
    store,
    deliveryOverride,
  }: {
    locationId: string
    online: OwnerFeeSettingsUpdate
    store?: OwnerFeeSettingsUpdate
    deliveryOverride?: OwnerFeeDeliveryOverrideUpdate | null
  }) => {
    const response =
      await OlympusClient.locations.location.updateOwnerFeeSettings({
        body: {
          online,
          store,
          ...(deliveryOverride !== undefined ? { deliveryOverride } : {}),
        },
        params: { locationId },
      })

    updateLocationData(locationId, { ownerFeeSettings: response.settings })
  }

  const updateLocationOwnerPos = async ({
    locationId,
  }: {
    locationId: string
  }) => {
    const location = await OlympusClient.locations.location.configureOwnerPos({
      body: {},
      params: { locationId },
    })

    updateLocationData(locationId, location)
  }

  const updateLocationPosFeatures = async ({
    locationId,
    updatedFeatures,
  }: {
    locationId: string
    updatedFeatures: { menuSync: { enabled: boolean } }
  }) => {
    const location = await OlympusClient.locations.location.updatePosFeatures({
      body: updatedFeatures,
      params: { locationId },
    })
    updateLocationData(locationId, location)
  }

  const removeLocationPos = async ({ locationId }: { locationId: string }) => {
    const location =
      await OlympusClient.locations.location.removePosIntegration({
        params: { locationId },
      })
    updateLocationData(locationId, location)
  }

  const restorePreviousLocationPos = async ({
    locationId,
    type,
    removedAt,
  }: {
    locationId: string
    // Optional selector for a specific archived POS; omit to restore the only
    // (or most-recent) one. Both must be provided together.
    type?: 'clover' | 'otter' | 'owner' | 'square' | 'uber'
    removedAt?: string
  }) => {
    const location = await OlympusClient.locations.location.restorePos({
      body: type && removedAt ? { removedAt, type } : {},
      params: { locationId },
    })
    updateLocationData(locationId, location)
  }

  const updateLocationSquare = async ({
    locationId,
    updatedSquareId,
  }: {
    locationId: string
    updatedSquareId: string | null
  }) => {
    const location = await OlympusClient.locations.location.linkSquareLocation({
      body: {
        squareLocationId: updatedSquareId,
      },
      params: {
        locationId,
      },
    })
    updateLocationData(locationId, location)
  }

  return {
    addLocation,
    getLocationById,
    getLocationNameById,
    getLocationPosById,
    locations,
    locationsWithoutStripeConnected,
    locationsWithoutYextConnected,
    refreshLocationData,
    removeLocationPos,
    restorePreviousLocationPos,
    updateLocation,
    updateLocationCateringNotificationRecipients,
    updateLocationCateringSettings,
    updateLocationGuestBearing,
    updateLocationLinks,
    updateLocationListingSyncTime,
    updateLocationOtter,
    updateLocationOwnerFeeSettings,
    updateLocationOwnerPos,
    updateLocationPosFeatures,
    updateLocationSquare,
  }
}
